import { useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { initiateReversal, lookupOutboundPayout } from '../../../services/ReversalService'
import { formatAmount, formatPerson } from './formatting'

const EMPTY_FORM = {
    mpesa_receipt_number: '',
    amount:               '',
    comment:              '',
}

const STEPS = { FORM: 'form', CONFIRM: 'confirm' }

const InitiateReversalModal = ({ show, onHide }) => {
    const queryClient = useQueryClient()
    const [form, setForm]           = useState(EMPTY_FORM)
    const [step, setStep]           = useState(STEPS.FORM)
    const [lookup, setLookup]       = useState(null)
    const [lookupError, setLookupError] = useState(null)
    const [lookupLoading, setLookupLoading] = useState(false)
    // Monotonic request id so a slow lookup that resolves out of order
    // cannot overwrite fresh state with stale data. See the same pattern
    // on CreateVendorReversalModal.
    const lookupRequestId = useRef(0)

    useEffect(() => {
        if (!show) {
            setForm(EMPTY_FORM)
            setStep(STEPS.FORM)
            setLookup(null)
            setLookupError(null)
            setLookupLoading(false)
            lookupRequestId.current += 1
        }
    }, [show])

    // Debounced re-lookup on every change to the receipt so the preview
    // panel and eligibility flags stay fresh without the user needing to
    // tab out of the field. Trailing 500ms of quiet after the last keystroke
    // fires the request; typing again cancels the pending fire.
    useEffect(() => {
        const receipt = form.mpesa_receipt_number.trim()
        if (!receipt) {
            setLookup(null)
            setLookupError(null)
            return
        }
        const timerId = window.setTimeout(() => { runLookup(receipt) }, 500)
        return () => window.clearTimeout(timerId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.mpesa_receipt_number])

    const runLookup = async (receipt) => {
        const trimmed = receipt.trim()
        if (!trimmed) {
            setLookup(null)
            setLookupError(null)
            return
        }
        const requestId = ++lookupRequestId.current
        setLookupLoading(true)
        setLookupError(null)
        try {
            const response = await lookupOutboundPayout(trimmed)
            if (lookupRequestId.current !== requestId) return
            setLookup(response.data)
            // Pre-fill the amount with the remaining refundable principal on
            // the first successful lookup so the common case (full reversal)
            // needs no extra typing.
            if (form.amount === '' && response.data?.remaining_to_refund) {
                setForm((f) => ({ ...f, amount: response.data.remaining_to_refund }))
            }
        } catch (err) {
            if (lookupRequestId.current !== requestId) return
            setLookup(null)
            const status = err?.response?.status
            if (status === 404) {
                setLookupError('No settled payout was found for that receipt.')
            } else if (status === 403) {
                setLookupError('You do not have permission to look up payouts.')
            } else {
                setLookupError(err?.response?.data?.message || 'Could not look up that receipt.')
            }
        } finally {
            if (lookupRequestId.current === requestId) {
                setLookupLoading(false)
            }
        }
    }

    const mutation = useMutation({
        mutationFn: (payload) => initiateReversal(payload),
        onSuccess: async () => {
            toast.success('Reversal initiated successfully.')
            await queryClient.invalidateQueries({ queryKey: ['reversals', 'admin-initiated'] })
            onHide()
        },
        onError: (err) => {
            const message = err?.response?.data?.message || 'Failed to initiate reversal.'
            toast.error(message)
        },
    })

    const eligibilityIssues = (() => {
        if (!lookup) return []
        const issues = []
        if (!lookup.eligibility.is_settled) {
            issues.push('This payout has not been settled by Safaricom yet, so there is nothing to reverse.')
        }
        if (lookup.eligibility.already_reversed_by_daraja) {
            issues.push('This payout has already been reversed through Safaricom, so it cannot be reversed again.')
        }
        if (Number(lookup.already_refunded_total) > 0) {
            issues.push('This payout has partial vendor refunds recorded against it, so a Safaricom reversal cannot be initiated on top. Ask engineering if a full reversal is still needed.')
        }
        return issues
    })()

    const formIsComplete =
        form.mpesa_receipt_number.trim() !== '' &&
        form.amount !== '' &&
        Number(form.amount) > 0 &&
        form.comment.trim() !== ''

    const amountWithinRemaining =
        lookup && Number(form.amount) > 0 &&
        Number(form.amount) <= Number(lookup.remaining_to_refund)

    const canContinue =
        formIsComplete &&
        !!lookup &&
        eligibilityIssues.length === 0 &&
        amountWithinRemaining

    const submit = () => {
        mutation.mutate({
            mpesa_receipt_number: form.mpesa_receipt_number.trim(),
            amount:               Number(form.amount),
            comment:              form.comment.trim(),
        })
    }

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            {step === STEPS.FORM && (
                <>
                    <Modal.Header closeButton>
                        <Modal.Title>Initiate reversal</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="text-muted mb-3">
                            Pull a completed payout back through Safaricom. The system calls the Safaricom Reversal API and the ledger updates automatically as soon as Safaricom confirms.
                        </p>
                        <form id="initiate-reversal-form" onSubmit={(e) => { e.preventDefault(); if (canContinue) setStep(STEPS.CONFIRM) }}>
                            <div className="mb-3">
                                <label className="form-label">Original Receipt ID <span className="text-danger">*</span></label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Receipt ID of the payout to reverse"
                                        maxLength={20}
                                        value={form.mpesa_receipt_number}
                                        onChange={(e) => {
                                            setForm({ ...form, mpesa_receipt_number: e.target.value })
                                            setLookup(null)
                                            setLookupError(null)
                                        }}
                                        onBlur={(e) => runLookup(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => runLookup(form.mpesa_receipt_number)}
                                        disabled={lookupLoading || form.mpesa_receipt_number.trim() === ''}
                                    >
                                        Look up
                                    </button>
                                </div>
                                {lookupLoading && (
                                    <div className="small text-muted mt-1">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                        Looking up payout...
                                    </div>
                                )}
                                {lookupError && (
                                    <div className="small text-danger mt-1">{lookupError}</div>
                                )}
                            </div>

                            {lookup && (
                                <div className="border rounded p-3 mb-3 bg-light">
                                    <div className="fw-semibold mb-2">Payout summary</div>
                                    <table className="table table-sm mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                        <tbody>
                                            <tr>
                                                <th scope="row" className="text-muted fw-normal" style={{ width: '50%' }}>Recipient</th>
                                                <td className="text-end" style={{ wordBreak: 'break-word' }}>{formatPerson(lookup.source_user)}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row" className="text-muted fw-normal">Original amount</th>
                                                <td className="text-end">{formatAmount(lookup.payout_amount)}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row" className="text-muted fw-normal">Already refunded</th>
                                                <td className="text-end">{formatAmount(lookup.already_refunded_total)}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row" className="fw-semibold">Remaining to refund</th>
                                                <td className="text-end fw-semibold">{formatAmount(lookup.remaining_to_refund)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {eligibilityIssues.length > 0 && (
                                <div className="alert alert-danger py-2 mb-3" role="alert">
                                    <ul className="mb-0 ps-3">
                                        {eligibilityIssues.map((issue, i) => <li key={i}>{issue}</li>)}
                                    </ul>
                                </div>
                            )}

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Amount (KES) <span className="text-danger">*</span></label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Amount to reverse"
                                        min="0.01"
                                        step="0.01"
                                        max={lookup?.remaining_to_refund}
                                        value={form.amount}
                                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                        required
                                    />
                                    {lookup && form.amount !== '' && !amountWithinRemaining && (
                                        <div className="small text-danger mt-1">
                                            Amount is greater than what remains on the payout.
                                        </div>
                                    )}
                                </div>
                                <div className="col-12">
                                    <label className="form-label">Comment <span className="text-danger">*</span></label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        placeholder="Short note on why this reversal is being initiated"
                                        maxLength={255}
                                        value={form.comment}
                                        onChange={(e) => setForm({ ...form, comment: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                        </form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide}>Cancel</Button>
                        <Button
                            type="submit"
                            form="initiate-reversal-form"
                            variant="primary"
                            disabled={!canContinue}
                        >
                            Continue
                        </Button>
                    </Modal.Footer>
                </>
            )}

            {step === STEPS.CONFIRM && lookup && (
                <>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm reversal</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="mb-3">
                            Once you confirm, our system will call Safaricom to pull the money back. The ledger will update automatically when Safaricom confirms. This cannot be undone from the UI.
                        </p>
                        <div className="border rounded mb-3">
                            <table className="table mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th scope="col" style={{ width: '65%' }}>What will happen</th>
                                        <th scope="col" style={{ width: '35%' }} className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ wordBreak: 'break-word' }}>{`Reversal request sent to Safaricom for ${formatPerson(lookup.source_user)}`}</td>
                                        <td className="text-end fw-semibold" style={{ wordBreak: 'break-word' }}>{formatAmount(form.amount || 0)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="small text-muted">
                            <div><strong>Original Receipt ID:</strong> {form.mpesa_receipt_number}</div>
                            <div><strong>Comment:</strong> {form.comment}</div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setStep(STEPS.FORM)}
                            disabled={mutation.isPending}
                        >
                            Back
                        </Button>
                        <Button
                            variant="primary"
                            onClick={submit}
                            disabled={mutation.isPending}
                        >
                            {mutation.isPending ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    Sending
                                </>
                            ) : (
                                'Initiate reversal'
                            )}
                        </Button>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    )
}

export default InitiateReversalModal
